"use client"

import { useCallback, useMemo } from "react"
import type { CommentOverlayMode, OverlayTab } from "@/components/candidate-profile-overlay"
import { applyOverlayAction, type CandidatesSearch, ValidateSearch } from "@/utils/candidate-profile-overlay-utils"

type SetSearchFn = (updater: (prev: CandidatesSearch) => CandidatesSearch) => void

type OnCommentChangeArg = {
  mode: CommentOverlayMode
  commentId?: string | null
}

/**
 * Keeps the candidate overlay addressable by URL. Pages provide their router
 * search state; the hook normalizes it and exposes focused overlay actions that
 * candidates and job pipeline can share without owning profile hydration.
 */
export function useCandidateOverlayUrlState({
  rawSearch,
  setSearch,
}: {
  rawSearch: Record<string, unknown>
  setSearch: SetSearchFn
}) {
  const normalized = useMemo<CandidatesSearch>(
    () => ValidateSearch(rawSearch),
    [rawSearch],
  )

  const candidateId = normalized.candidateId ?? null
  const tab: OverlayTab = normalized.tab ?? "none"
  const commentMode: CommentOverlayMode = normalized.commentMode ?? "none"
  const commentId = normalized.commentId

  const dispatch = useCallback(
    (action: Parameters<typeof applyOverlayAction>[1]) => {
      setSearch((prev) => {
        const next = applyOverlayAction(prev ?? {}, action)
        if (!next.candidateId) return {}
        return next
      })
    },
    [setSearch],
  )

  const openCandidate = useCallback(
    (id: string) => dispatch({ type: "selectCandidate", candidateId: id }),
    [dispatch],
  )

  /*
    A candidate opened straight onto a tab or a single comment — from an
    activity row that names what happened, say. One search update: selecting
    the candidate and then setting the tab as two navigations would let the
    second read the search from before the first.
  */
  const openCandidateAt = useCallback(
    (id: string, at: { tab?: OverlayTab; commentId?: string }) => {
      setSearch((prev) => {
        const selected = applyOverlayAction(prev ?? {}, { type: "selectCandidate", candidateId: id })
        if (at.commentId) {
          return applyOverlayAction(selected, { type: "setComment", mode: "view", commentId: at.commentId })
        }
        return at.tab ? applyOverlayAction(selected, { type: "setTab", tab: at.tab }) : selected
      })
    },
    [setSearch],
  )

  const closeOverlay = useCallback(() => dispatch({ type: "close" }), [dispatch])

  const setTab = useCallback(
    (nextTab: OverlayTab) => dispatch({ type: "setTab", tab: nextTab }),
    [dispatch],
  )

  const setComment = useCallback(
    (next: OnCommentChangeArg) => {
      if (next.mode === "none") {
        dispatch({ type: "setComment", mode: "none" })
        return
      }

      if (next.mode === "create") {
        dispatch({ type: "setComment", mode: "create" })
        return
      }

      // The whole list, which the Comments count opens. Like "create" it needs
      // no id; without this branch it fell through to the id check below and was
      // dropped in silence.
      if (next.mode === "list") {
        dispatch({ type: "setComment", mode: "list" })
        return
      }

      if (!next.commentId) return

      dispatch({
        type: "setComment",
        mode: "view",
        commentId: next.commentId,
      })
    },
    [dispatch],
  )

  return {
    candidateId,
    tab,
    commentMode,
    commentId,
    openCandidate,
    openCandidateAt,
    closeOverlay,
    setTab,
    setComment,
  }
}
